import { useEffect, useState } from "react";
import { Copy, Eye, EyeOff, KeyRound, Search, Trash2, UserCog } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import CriticalPinDialog from "../CriticalPinDialog";

const ROLE_LABEL = { admin: "Admin", supplier: "Tedarikçi", buyer: "Mağaza" };
const ROLE_STYLE = {
    admin: "border-red-200 bg-red-100 text-red-700",
    supplier: "border-amber-200 bg-amber-100 text-amber-800",
    buyer: "border-emerald-200 bg-emerald-100 text-emerald-800",
};

const Chip = ({ active, onClick, children, tid }) => (
    <button onClick={onClick} data-testid={tid} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "hover:border-terra-500/50"}`}>{children}</button>
);

const genPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    const arr = new Uint32Array(12);
    crypto.getRandomValues(arr);
    return Array.from(arr, (n) => chars[n % chars.length]).join("");
};

export default function UsersTab() {
    const [users, setUsers] = useState(null);
    const [busy, setBusy] = useState(null);
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [pwTarget, setPwTarget] = useState(null);
    const [newPw, setNewPw] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [pinAction, setPinAction] = useState(null);
    const [result, setResult] = useState(null);

    const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const startSet = (u) => { setPwTarget(u); setNewPw(""); setShowPw(false); };

    const submitPw = (pin) => {
        const u = pwTarget;
        setBusy(u.user_id);
        api.put(`/admin/users/${u.user_id}/password`, { password: newPw }, { headers: { "X-Critical-Pin": pin } })
            .then(() => {
                toast.success(`${u.name} için yeni şifre belirlendi`);
                setPinAction(null);
                setPwTarget(null);
                setResult({ name: u.name, email: u.email, password: newPw });
                setNewPw("");
                load();
            })
            .catch((e) => toast.error(e.response?.data?.detail || "İşlem başarısız"))
            .finally(() => setBusy(null));
    };

    const requestPin = () => {
        if (newPw.length < 8) { toast.error("Şifre en az 8 karakter olmalıdır"); return; }
        setPinAction({ label: `"${pwTarget.name} (${pwTarget.email})" kullanıcısının şifresini değiştirme — kullanıcının tüm oturumları kapatılacak`, run: submitPw });
    };

    const copyPw = () => {
        navigator.clipboard?.writeText(result.password).then(() => toast.success("Şifre kopyalandı")).catch(() => toast.error("Kopyalanamadı"));
    };

    const deleteUser = (u, pin) => {
        setBusy(u.user_id);
        api.delete(`/admin/records/users/${u.user_id}`, { headers: { "X-Critical-Pin": pin } })
            .then(() => { toast.success(`${u.name} silindi (geri dönüşüm kutusuna taşındı)`); setPinAction(null); load(); })
            .catch((e) => toast.error(e.response?.data?.detail || "Silme başarısız"))
            .finally(() => setBusy(null));
    };

    const requestDelete = (u) => {
        setPinAction({ label: `"${u.name} (${u.email})" kullanıcısını silme — oturumları, sepeti ve bildirimleri de silinir; kayıt Geri Dönüşüm Kutusu'na taşınır`, run: (pin) => deleteUser(u, pin) });
    };

    const filtered = (users || []).filter((u) => {
        const q = search.toLowerCase();
        const matchesQ = !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.org_name || "").toLowerCase().includes(q);
        const matchesR = roleFilter === "all" || u.role === roleFilter;
        return matchesQ && matchesR;
    });

    return (
        <div data-testid="admin-users-tab">
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ad, e-posta veya firma ara..." className="h-9 w-64 pl-8 text-sm" data-testid="users-search" />
                </div>
                <Chip active={roleFilter === "all"} onClick={() => setRoleFilter("all")} tid="users-filter-all">Tümü</Chip>
                <Chip active={roleFilter === "buyer"} onClick={() => setRoleFilter("buyer")} tid="users-filter-buyer">Mağaza</Chip>
                <Chip active={roleFilter === "supplier"} onClick={() => setRoleFilter("supplier")} tid="users-filter-supplier">Tedarikçi</Chip>
                <Chip active={roleFilter === "admin"} onClick={() => setRoleFilter("admin")} tid="users-filter-admin">Admin</Chip>
                <span className="ml-auto text-xs text-muted-foreground">{filtered.length} kullanıcı</span>
            </div>
            {!users ? (
                <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-muted" />)}</div>
            ) : (
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                    <div className="max-h-[520px] overflow-x-auto overflow-y-auto">
                        <table className="w-full text-sm" data-testid="users-table">
                            <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                                <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="p-4">Kullanıcı</th><th className="p-4">Rol</th><th className="p-4">Firma</th><th className="p-4">Şifre Durumu</th><th className="p-4 text-right">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((u) => (
                                    <tr key={u.user_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`user-row-${u.user_id}`}>
                                        <td className="p-4">
                                            <p className="font-semibold">{u.name}</p>
                                            <p className="text-xs text-muted-foreground">{u.email}</p>
                                        </td>
                                        <td className="p-4"><Badge variant="outline" className={`text-[10px] ${ROLE_STYLE[u.role] || ""}`} data-testid={`user-role-${u.user_id}`}>{ROLE_LABEL[u.role] || u.role}</Badge></td>
                                        <td className="p-4 text-muted-foreground">{u.org_name || "—"}</td>
                                        <td className="p-4" data-testid={`user-pwstatus-${u.user_id}`}>
                                            {u.has_password
                                                ? <Badge variant="outline" className="border-emerald-200 bg-emerald-100 text-[10px] text-emerald-800">Şifre Tanımlı</Badge>
                                                : <Badge variant="outline" className="border-slate-200 bg-slate-100 text-[10px] text-slate-600">Yalnızca Google</Badge>}
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button size="sm" variant="outline" disabled={busy === u.user_id} onClick={() => startSet(u)} data-testid={`user-setpw-${u.user_id}`}>
                                                    <KeyRound className="mr-1.5 h-3.5 w-3.5" /> Şifre Belirle
                                                </Button>
                                                <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" disabled={busy === u.user_id} onClick={() => requestDelete(u)} data-testid={`user-delete-${u.user_id}`}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!filtered.length && <tr><td colSpan={5} className="p-16 text-center text-sm text-muted-foreground"><UserCog className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />Kullanıcı bulunamadı.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Dialog open={!!pwTarget} onOpenChange={(o) => { if (!o) setPwTarget(null); }}>
                <DialogContent className="max-w-md" data-testid="user-pw-dialog">
                    <DialogHeader>
                        <DialogTitle>Yeni Şifre Belirle</DialogTitle>
                    </DialogHeader>
                    {pwTarget && (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">{pwTarget.name}</span> ({pwTarget.email}) için yeni bir giriş şifresi belirleyin. Kullanıcının mevcut tüm oturumları kapatılacaktır.</p>
                            <div className="relative">
                                <Input type={showPw ? "text" : "password"} value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Yeni şifre (en az 8 karakter)" className="pr-10" data-testid="user-pw-input" />
                                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="user-pw-toggle">
                                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            <Button type="button" variant="outline" size="sm" onClick={() => { setNewPw(genPassword()); setShowPw(true); }} data-testid="user-pw-generate">Rastgele Güçlü Şifre Oluştur</Button>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPwTarget(null)} data-testid="user-pw-cancel">Vazgeç</Button>
                        <Button onClick={requestPin} disabled={!!busy} data-testid="user-pw-submit">Devam Et</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <CriticalPinDialog open={!!pinAction} onClose={() => setPinAction(null)} onSubmit={(pin) => pinAction?.run(pin)} actionLabel={pinAction?.label || ""} busy={!!busy} />

            <Dialog open={!!result} onOpenChange={(o) => { if (!o) setResult(null); }}>
                <DialogContent className="max-w-md" data-testid="user-pw-result-dialog">
                    <DialogHeader>
                        <DialogTitle>Şifre Güncellendi</DialogTitle>
                    </DialogHeader>
                    {result && (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">{result.name}</span> ({result.email}) artık bu şifreyle giriş yapabilir. Güvenlik gereği şifreler veritabanında şifreli saklanır — bu şifre yalnızca şimdi gösterilir, lütfen kopyalayıp kullanıcıya iletin.</p>
                            <div className="flex items-center gap-2 rounded-xl border bg-muted/60 p-3">
                                <code className="flex-1 break-all font-mono text-sm font-bold" data-testid="user-pw-result">{result.password}</code>
                                <Button size="sm" variant="outline" onClick={copyPw} data-testid="user-pw-copy"><Copy className="mr-1.5 h-3.5 w-3.5" /> Kopyala</Button>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button onClick={() => setResult(null)} data-testid="user-pw-result-close">Kapat</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
